import React, { useEffect, useMemo, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./auth/AuthContext.jsx";
import { FeatureProvider } from "./auth/FeatureContext.jsx";
import RouteGuard from "./auth/RouteGuard.jsx";
import Login from "./screens/Login.jsx";
import ForgotPassword from "./screens/ForgotPassword.jsx";
import ResetPassword from "./screens/ResetPassword.jsx";
import SetPassword from "./screens/SetPassword.jsx";
import MagicLinkLanding from "./screens/MagicLinkLanding.jsx";
import VerifyEmail from "./auth/VerifyEmail.jsx";
import SsoCallback from "./auth/SsoCallback.jsx";
import SsoHandoff from "./auth/SsoHandoff.jsx";
import { motion, AnimatePresence } from "framer-motion";

import RepShell from "./shells/RepShell.jsx";
import FinanceShell from "./shells/FinanceShell.jsx";
import CompAdminShell from "./shells/CompAdminShell.jsx";
import ManagerShell from "./shells/ManagerShell.jsx";
import TenantAdminShell from "./components/tenant-admin/TenantAdminShell.jsx";
import OnboardingShell from "./shells/OnboardingShell.jsx";
import PlatformAdminShell from "./shells/PlatformAdminShell.jsx";

import {
  AlertCircle,
  AlertTriangle,
  BadgeDollarSign,
  Bell,
  Building2,
  Calculator,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Command,
  DatabaseZap,
  FileCheck2,
  FileText,
  HelpCircle,
  Info,
  Layers3,
  Lock,
  Menu,
  MessageSquareText,
  Moon,
  Network,
  PanelRightOpen,
  Search,
  Settings,
  Shield,
  ShieldCheck,
  Sparkles,
  Sun,
  Target,
  Upload,
  UserRound,
  Users,
  WalletCards,
  Workflow,
  X,
  XCircle,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Shell imports — stubs until each shell is built (Steps S73-UI-04 through 07)
// Each shell lives in ui/src/shells/ per spec v1.3 §9 folder structure.
// No cross-shell imports permitted (DD-04).
// ---------------------------------------------------------------------------
// import RepShell from "./shells/RepShell.jsx";          // S73-UI-04
// import ManagerShell from "./shells/ManagerShell.jsx";  // S73-UI-05 BLOCKED
// import FinanceShell from "./shells/FinanceShell.jsx";  // S73-UI-06
// import CompAdminShell from "./shells/CompAdminShell.jsx"; // S73-UI-07

// ---------------------------------------------------------------------------
// Deferred holding screen — tenant_super_admin only.
// Shown until TenantAdminShell backend endpoints exist (spec v1.3 §1.3).
// ---------------------------------------------------------------------------
function DeferredHoldingScreen() {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      flexDirection: "column",
      gap: "12px",
      color: "var(--color-text-tertiary)",
      fontSize: "var(--font-size-sm)",
    }}>
      <div style={{ fontWeight: 600, fontSize: "1rem", color: "var(--color-text-primary)" }}>
        Your workspace is being configured.
      </div>
      <div>Contact your administrator.</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shell placeholder — used for shells not yet built.
// Replaced shell-by-shell as each is completed.
// ---------------------------------------------------------------------------
function ShellPlaceholder({ shellName }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      flexDirection: "column",
      gap: "8px",
      color: "var(--color-text-tertiary)",
      fontSize: "var(--font-size-sm)",
    }}>
      <div style={{ fontWeight: 600, color: "var(--color-text-primary)" }}>
        {shellName}
      </div>
      <div>Coming soon.</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DecisionFlow Enterprise App — existing monolithic comp admin shell.
// Stays here until S73-UI-07 when it is migrated into CompAdminShell.
// ---------------------------------------------------------------------------

const ROLE_PROFILES = {
  comp_admin: {
    label: "Comp Admin",
    description: "Compensation configuration and operations",
    allowedGroups: ["Configuration", "Data Setup", "Execution", "Governance"],
  },
  tenant_super_admin: {
    label: "Tenant Super Admin",
    description: "Users, roles, and scope only",
    allowedGroups: ["Governance"],
  },
  finance: {
    label: "Finance",
    description: "Statements, payroll, exposure review",
    allowedGroups: ["Execution", "Governance"],
  },
  manager: {
    label: "Manager",
    description: "Team readiness, statements, and payout review",
    allowedGroups: ["Execution", "Governance"],
  },
  rep: {
    label: "Rep",
    description: "Own statement and payout summary only",
    allowedGroups: ["Execution"],
  },
};

const STAGE_GROUPS = [
  {
    name: "Configuration",
    caption: "Wizard-only setup",
    stages: [
      { id: 0, name: "Tenant Provisioning", icon: Building2, status: "Complete" },
      { id: 1, name: "Internal Access", icon: ShieldCheck, status: "Complete" },
      { id: 4, name: "Fiscal Calendar", icon: CalendarDays, status: "Complete" },
      { id: 5, name: "Organization", icon: Network, status: "Complete" },
      { id: 6, name: "Sales Roles", icon: BadgeDollarSign, status: "Complete" },
      { id: 9, name: "Plan Wizard", icon: FileCheck2, status: "Draft" },
      { id: 10, name: "Plan Approval", icon: ClipboardCheck, status: "Pending" },
    ],
  },
  {
    name: "Data Setup",
    caption: "Imports and assignment",
    stages: [
      { id: 7, name: "Position Load", icon: Upload, status: "Warning" },
      { id: 8, name: "Position Detail", icon: UserRound, status: "Ready" },
      { id: 11, name: "Plan Assignment", icon: Workflow, status: "Ready" },
      { id: 12, name: "Quota Load", icon: Target, status: "Warning" },
      { id: 13, name: "Credit Config", icon: DatabaseZap, status: "Ready" },
      { id: 14, name: "Credit Load", icon: Upload, status: "Ready" },
    ],
  },
  {
    name: "Execution",
    caption: "Run, release, export",
    stages: [
      { id: 15, name: "Readiness", icon: AlertTriangle, status: "Blocked" },
      { id: 16, name: "Calculation", icon: Calculator, status: "Preview" },
      { id: 17, name: "Statements", icon: FileText, status: "Draft" },
      { id: 18, name: "Payroll", icon: WalletCards, status: "Draft" },
      { id: 19, name: "Certification", icon: CheckCircle2, status: "Not Ready" },
    ],
  },
  {
    name: "Governance",
    caption: "Access and controls",
    stages: [
      { id: 2, name: "Tenant Super Admin", icon: Shield, status: "Complete" },
      { id: 3, name: "User & Access", icon: Users, status: "Ready" },
    ],
  },
];

const ALL_STAGES = STAGE_GROUPS.flatMap((g) => g.stages.map((s) => ({ ...s, group: g.name })));

const statusClass = {
  Complete: "bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-950 dark:text-emerald-300 dark:ring-emerald-400/20",
  Ready: "bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-950 dark:text-blue-300 dark:ring-blue-400/20",
  Warning: "bg-amber-50 text-amber-800 ring-amber-600/20 dark:bg-amber-950 dark:text-amber-300 dark:ring-amber-400/20",
  Blocked: "bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-950 dark:text-red-300 dark:ring-red-400/20",
  Draft: "bg-slate-100 text-slate-700 ring-slate-600/20 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-400/20",
  Pending: "bg-violet-50 text-violet-700 ring-violet-600/20 dark:bg-violet-950 dark:text-violet-300 dark:ring-violet-400/20",
  Preview: "bg-cyan-50 text-cyan-700 ring-cyan-600/20 dark:bg-cyan-950 dark:text-cyan-300 dark:ring-cyan-400/20",
  "Not Ready": "bg-slate-100 text-slate-700 ring-slate-600/20 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-400/20",
};

function Pill({ children, tone = "Ready" }) {
  return <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusClass[tone] || statusClass.Ready}`}>{children}</span>;
}

function Button({ children, variant = "primary", disabled = false, onClick, className = "" }) {
  const base = "inline-flex items-center justify-center gap-2 rounded-xl px-3.5 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50";
  const variants = {
    primary: "bg-slate-950 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200",
    secondary: "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
    ghost: "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800",
    danger: "bg-red-600 text-white hover:bg-red-700",
  };
  return <button onClick={onClick} disabled={disabled} className={`${base} ${variants[variant]} ${className}`}>{children}</button>;
}

function Card({ title, children, action, className = "" }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900 ${className}`}>
      {(title || action) && (
        <div className="mb-3 flex items-center justify-between">
          {title && <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h2>}
          {action && <div className="flex items-center gap-2">{action}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

function Field({ label, value, mono = false, mode }) {
  return (
    <div>
      <div className="mb-0.5 text-xs font-medium text-slate-500">{label}</div>
      <div className={`text-sm font-semibold text-slate-900 dark:text-slate-100 ${mono ? "font-mono text-xs" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

function ThemeButton({ dark, setDark }) {
  const toggle = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("decisionflow-theme", next ? "dark" : "light");
  };
  return (
    <button onClick={toggle} className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800">
      {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

function Grid({ columns, rows }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">{columns.map((c) => <th key={c} className="px-3 py-2 text-left text-xs font-semibold text-slate-500">{c}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-b border-slate-100 last:border-0 dark:border-slate-800">{r.map((cell, j) => <td key={j} className="px-3 py-2 text-slate-700 dark:text-slate-300">{cell}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function ValidationList({ mode, items }) {
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-2 text-sm">
          {item.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> : item.warn ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />}
          <span className="text-slate-700 dark:text-slate-300">{mode === "dev" ? item.text : item.prod}</span>
        </div>
      ))}
    </div>
  );
}

function StateStepper({ mode, states, active }) {
  return (
    <div className="mb-4 flex items-center gap-1">
      {states.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`rounded-full px-3 py-1 text-xs font-semibold ${i === active ? "bg-slate-950 text-white dark:bg-white dark:text-slate-950" : i < active ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" : "bg-slate-100 text-slate-400 dark:bg-slate-800"}`}>{s}</div>
          {i < states.length - 1 && <ChevronRight className="h-3 w-3 text-slate-300" />}
        </React.Fragment>
      ))}
    </div>
  );
}

function TopBar({ active, role, setRole, mode, dark, setDark, setMobileOpen, copilotOpen, setCopilotOpen, userName }) {
  return (
    <header className="flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-center gap-3">
        <button onClick={() => setMobileOpen(true)} className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 lg:hidden"><Menu className="h-5 w-5" /></button>
        <BadgeDollarSign className="h-6 w-6 text-slate-950 dark:text-white" />
        <span className="text-sm font-bold tracking-tight text-slate-950 dark:text-white">DecisionFlow</span>
        {mode === "dev" && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-700 dark:bg-violet-950 dark:text-violet-300">dev</span>}
      </div>
      <div className="flex items-center gap-1">
        <div className="hidden items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-400 dark:border-slate-800 dark:bg-slate-950 sm:flex"><Search className="h-3.5 w-3.5" /><span>Search…</span></div>
        <button className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"><Bell className="h-4 w-4" /></button>
        <ThemeButton dark={dark} setDark={setDark} />
        <button onClick={() => setCopilotOpen(!copilotOpen)} className="hidden rounded-xl p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 xl:block"><PanelRightOpen className="h-4 w-4" /></button>
        <div className="ml-1 flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-1.5 dark:border-slate-800">
          {mode === "dev" && setRole ? (
            <select value={role} onChange={(e) => setRole(e.target.value)} className="bg-transparent text-xs font-semibold text-slate-700 dark:text-slate-200 focus:outline-none">
              {Object.entries(ROLE_PROFILES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          ) : (
            <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{userName ?? ROLE_PROFILES[role]?.label ?? role}</span>
          )}
        </div>
      </div>
    </header>
  );
}

function LeftNav({ stage, setStage, role, mobileOpen, setMobileOpen, mode }) {
  const allowed = ROLE_PROFILES[role]?.allowedGroups ?? [];
  const visible = STAGE_GROUPS.filter((g) => allowed.includes(g.name));
  return (
    <>
      {mobileOpen && <div className="fixed inset-0 z-30 bg-slate-950/50 lg:hidden" onClick={() => setMobileOpen(false)} />}
      <aside className={`fixed inset-y-0 left-0 z-40 w-56 overflow-y-auto border-r border-slate-200 bg-white pt-16 dark:border-slate-800 dark:bg-slate-900 lg:static lg:pt-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} transition-transform lg:translate-x-0`}>
        <nav className="p-3 space-y-4">
          {visible.map((g) => (
            <div key={g.name}>
              <div className="mb-1 px-2 text-xs font-semibold uppercase tracking-wider text-slate-400">{g.name}</div>
              {g.stages.map((s) => {
                const Icon = s.icon;
                const active = stage === s.id;
                return (
                  <button key={s.id} onClick={() => { setStage(s.id); setMobileOpen(false); }} className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm font-medium transition ${active ? "bg-slate-950 text-white dark:bg-white dark:text-slate-950" : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"}`}>
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{s.name}</span>
                    {mode === "dev" && <span className="ml-auto text-xs opacity-40">{s.id}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}

function ContextHeader({ active, mode, userName, userRole, tenantName }) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
      <div>
        <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">{active.name}</h1>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          {tenantName && <span>{tenantName}</span>}
          {tenantName && <span>·</span>}
          {userRole && <span className="capitalize">{userRole.replace("_", " ")}</span>}
          {userName && <><span>·</span><span>{userName}</span></>}
          {mode === "dev" && <><span>·</span><span className="font-mono">stage {active.id}</span></>}
        </div>
      </div>
      <Pill tone={active.status}>{active.status}</Pill>
    </div>
  );
}

function CopilotPanel({ open, active, mode, role }) {
  const [input, setInput] = useState("");
  if (!open) return null;
  return (
    <div className="flex h-full w-80 flex-col border-l border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800">
        <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-500" /><span className="text-sm font-semibold">Copilot</span></div>
        {mode === "dev" && <span className="text-xs text-slate-400">{role}</span>}
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600 dark:bg-slate-950 dark:text-slate-300">Ask me anything about this stage, your data, or how to proceed.</div>
      </div>
      <div className="border-t border-slate-200 p-3 dark:border-slate-800">
        <div className="flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask Copilot…" className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-800 dark:bg-slate-950 focus:outline-none" />
          <Button disabled={!input}><Sparkles className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}

function TenantSuperAdmin({ mode }) {
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card title="User & Access Management" className="lg:col-span-2">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Active Users" value="—" />
          <Field label="Pending Invitations" value="—" />
        </div>
        <div className="mt-4"><Button disabled>Invite User</Button></div>
      </Card>
      <InsightPanel mode={mode} />
    </div>
  );
}

function PositionDetail({ mode }) {
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card title="Position Detail" className="lg:col-span-2">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Total Positions" value="—" />
          <Field label="Missing Ramp" value="—" />
        </div>
      </Card>
      <InsightPanel mode={mode} />
    </div>
  );
}

function PlanApproval({ mode }) {
  const [step, setStep] = useState(0);
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card title="Plan Approval" className="lg:col-span-2" action={<Pill tone={step === 2 ? "Complete" : step === 1 ? "Pending" : "Draft"}>{["Draft", "Pending", "Approved"][step]}</Pill>}>
        <StateStepper mode={mode} states={["Draft", "Submitted", "Approved"]} active={step} />
        <div className="grid gap-3 sm:grid-cols-3"><Field label="Plan Name" value="—" /><Field label="Version" value="—" /><Field label="Components" value="—" /></div>
        <div className="mt-4 flex gap-2">{step === 0 && <Button onClick={() => setStep(1)}>Submit for Approval</Button>}{step === 1 && <><Button variant="danger" onClick={() => setStep(0)}>Reject</Button><Button onClick={() => setStep(2)}>Approve</Button></>}{step === 2 && <Button variant="secondary">View Approved Plan</Button>}</div>
      </Card>
      <InsightPanel mode={mode} />
    </div>
  );
}

function QuotaLoad({ mode }) {
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card title="Quota Load" className="lg:col-span-2">
        <div className="grid gap-3 sm:grid-cols-2"><Field label="Rows Loaded" value="—" /><Field label="Periods Affected" value="—" /></div>
      </Card>
      <InsightPanel mode={mode} />
    </div>
  );
}

function ReadinessScreen({ mode }) {
  const gates = [
    { ok: false, warn: false, text: "G-01: Wizard certification complete", prod: "Wizard certification required" },
    { ok: false, warn: false, text: "G-02: Credit loaded for active period", prod: "Credit data not loaded" },
    { ok: false, warn: true,  text: "G-03: Quota loaded for active period", prod: "Quota data incomplete" },
    { ok: true,  warn: false, text: "G-04: Plan component approved", prod: "Plan approved" },
    { ok: true,  warn: false, text: "G-05: Rate table approved", prod: "Rate table approved" },
  ];
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card title="Readiness Check" className="lg:col-span-2" action={<Pill tone="Blocked">Blocked</Pill>}>
        <ValidationList mode={mode} items={gates} />
        <div className="mt-4 flex gap-2"><Button disabled>Run Calculation</Button><Button variant="secondary">Re-check</Button></div>
      </Card>
      <InsightPanel mode={mode} />
    </div>
  );
}

function CalculationConsole({ mode }) {
  const [runType, setRunType] = useState("preview");
  const [modal, setModal] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const gates = [
    { ok: true,  warn: false, text: "All readiness gates passed", prod: "Ready to run" },
    { ok: false, warn: true,  text: "8 quota rows pending review", prod: "Review quota before final" },
  ];
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card title="Calculation Console" className="lg:col-span-2">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block"><div className="mb-1 text-xs font-medium text-slate-500">Run Type</div><select value={runType} onChange={(e) => e.target.value === "final" ? setModal(true) : (setRunType("preview"), setConfirmed(false))} className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-800 dark:bg-slate-950"><option value="preview">Preview</option><option value="final">Final</option></select></label>
        </div>
        <div className="mt-4"><ValidationList mode={mode} items={gates} /></div>
        <div className="mt-4 flex gap-2"><Button disabled>Run Calculation</Button><Button variant="secondary">Validate Only</Button></div>
      </Card>
      <InsightPanel mode={mode} />
      <AnimatePresence>{modal && <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/60 p-4"><motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }} className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-slate-900"><h3 className="text-lg font-bold">Final Run Confirmation</h3><p className="mt-2 text-sm text-slate-600 dark:text-slate-300">Final runs produce payroll-ready outputs. Statements will be generated from this run. This cannot be undone without initiating a new calculation run.</p><div className="mt-5 flex justify-end gap-2"><Button variant="secondary" onClick={() => setModal(false)}>Cancel</Button><Button onClick={() => { setConfirmed(true); setRunType("final"); setModal(false); }}>Confirm Final</Button></div></motion.div></div>}</AnimatePresence>
    </div>
  );
}

function StatementPublishing({ mode }) {
  const [state, setState] = useState(0);
  const [sameUser, setSameUser] = useState(false);
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card title="Statement Publishing" className="lg:col-span-2" action={<Pill tone={state === 2 ? "Complete" : state === 1 ? "Pending" : "Draft"}>{["Draft", "Pending", "Released"][state]}</Pill>}>
        <StateStepper mode={mode} states={["Generated Draft", "Pending Approval", "Released"]} active={state} />
        <div className="grid gap-3 sm:grid-cols-3"><Field label="Draft" value={state === 0 ? "120" : "0"} /><Field label="Pending Approval" value={state === 1 ? "120" : "0"} /><Field label="Released" value={state === 2 ? "120" : "0"} /></div>
        <div className="mt-4 rounded-xl bg-amber-50 p-3 text-sm font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300">Reps cannot see statements until Released.</div>
        {mode === "dev" && <label className="mt-4 flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:border-slate-800 dark:bg-slate-950"><input type="checkbox" checked={sameUser} onChange={(e) => setSameUser(e.target.checked)} /> Simulate same releaser/submitter</label>}
        <div className="mt-4 flex gap-2">{state === 0 && <Button onClick={() => setState(1)}>Submit for Approval</Button>}{state === 1 && <><Button variant="danger" onClick={() => setState(0)}>Reject</Button><Button disabled={sameUser} onClick={() => setState(2)}>Release</Button></>}{state === 2 && <Button variant="secondary">View Released Statements</Button>}</div>
      </Card>
      <InsightPanel mode={mode} />
    </div>
  );
}

function InsightPanel({ mode }) {
  return (
    <Card title="System Intelligence" action={<Sparkles className="h-4 w-4 text-violet-500" />}>
      <ValidationList mode={mode} items={[
        { ok: true, text: "Plan approval path is valid", prod: "Approval path looks good" },
        { ok: false, warn: true, text: "8 quota rows pending", prod: "8 quota rows need attention" },
        { ok: true, text: "No plan structure imported", prod: "Configuration integrity preserved" },
      ]} />
      <div className="mt-4 rounded-xl border border-slate-200 p-3 dark:border-slate-800">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Suggested action</div>
        <div className="mt-1 text-sm font-medium">Resolve quota blockers before final calculation.</div>
      </div>
    </Card>
  );
}

function GenericStage({ active, setStage, mode }) {
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card title={active.name} className="lg:col-span-2" action={<Pill tone={active.status}>{active.status}</Pill>}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Primary Actor" value={active.id <= 2 ? "Platform / Tenant Admin" : "Comp Admin"} />
          <Field label="Layer" value={active.id <= 6 || active.id === 9 || active.id === 10 ? "Configuration Layer" : "Data / Execution Layer"} />
          <Field label="Policy Gate" value={active.id === 9 ? "Wizard-only plan definition" : "RBAC, audit, readiness"} />
          {mode === "dev" && <Field label="API Surface" value="FastAPI /api/v1" mono mode={mode} />}
        </div>
        <div className="mt-4"><Grid columns={["Area", "Result", "Status"]} rows={[["Configuration", "Validated", <Pill tone="Complete">Complete</Pill>], ["Audit", "Captured", <Pill>Ready</Pill>], ["Readiness", active.status, <Pill tone={active.status}>{active.status}</Pill>]]} /></div>
        <div className="mt-4 flex gap-2"><Button onClick={() => setStage(Math.min(19, active.id + 1))}>Continue</Button><Button variant="secondary">Validate</Button></div>
      </Card>
      <InsightPanel mode={mode} />
    </div>
  );
}

function Screen({ active, setStage, mode }) {
  switch (active.id) {
    case 2: return <TenantSuperAdmin mode={mode} />;
    case 8: return <PositionDetail mode={mode} />;
    case 10: return <PlanApproval mode={mode} />;
    case 12: return <QuotaLoad mode={mode} />;
    case 15: return <ReadinessScreen mode={mode} />;
    case 16: return <CalculationConsole mode={mode} />;
    case 17: return <StatementPublishing mode={mode} />;
    default: return <GenericStage active={active} setStage={setStage} mode={mode} />;
  }
}

export function DecisionFlowEnterpriseApp() {
  const { user } = useAuth();
  const [dark, setDark] = useState(false);
  const mode = import.meta.env.VITE_APP_MODE === "dev" ? "dev" : "prod";

  const authRole = user?.user_type ?? "comp_admin";
  const [devRole, setDevRole] = useState(authRole);
  const role = mode === "dev" ? devRole : authRole;

  const [stage, setStage] = useState(10);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(true);

  const userName = user?.full_name ?? user?.email ?? null;
  const userRole = role;
  const tenantName = user?.tenant_name ?? null;

  useEffect(() => {
    const saved = localStorage.getItem("decisionflow-theme");
    const prefers = window.matchMedia?.("(prefers-color-scheme: dark)").matches;
    const shouldDark = saved ? saved === "dark" : prefers;
    setDark(shouldDark);
    document.documentElement.classList.toggle("dark", shouldDark);
  }, []);

  const visibleStages = useMemo(() => {
    const allowed = ROLE_PROFILES[role]?.allowedGroups ?? [];
    return ALL_STAGES.filter((s) => allowed.includes(s.group));
  }, [role]);

  useEffect(() => {
    if (!visibleStages.some((s) => s.id === stage)) setStage(visibleStages[0]?.id ?? 2);
  }, [role, stage, visibleStages]);

  const active = ALL_STAGES.find((s) => s.id === stage) || visibleStages[0] || ALL_STAGES[0];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-slate-50">
      <TopBar active={active} role={role} setRole={mode === "dev" ? setDevRole : undefined} mode={mode} dark={dark} setDark={setDark} setMobileOpen={setMobileOpen} copilotOpen={copilotOpen} setCopilotOpen={setCopilotOpen} userName={userName} />
      <div className="flex min-h-[calc(100vh-64px)]">
        <LeftNav stage={stage} setStage={setStage} role={role} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} mode={mode} />
        <main className="min-w-0 flex-1 p-4 lg:p-6">
          <ContextHeader active={active} mode={mode} userName={userName} userRole={userRole} tenantName={tenantName} />
          <motion.div key={`${stage}-${mode}-${role}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18 }}>
            <Screen active={active} setStage={setStage} mode={mode} />
          </motion.div>
        </main>
        <div className="hidden xl:block"><CopilotPanel open={copilotOpen} active={active} mode={mode} role={role} /></div>
      </div>
      <div className="fixed bottom-4 right-4 xl:hidden"><Button onClick={() => setCopilotOpen(!copilotOpen)}><Sparkles className="h-4 w-4" /> Copilot</Button></div>
      <AnimatePresence>{copilotOpen && <div className="xl:hidden"><div className="fixed inset-0 z-50 bg-slate-950/50" onClick={() => setCopilotOpen(false)} /><motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} className="fixed right-0 top-0 z-50 h-full w-full max-w-sm overflow-auto"><CopilotPanel open={true} active={active} mode={mode} role={role} /></motion.div></div>}</AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root App — defines Routes per DD-03 and spec v1.3 §2.1
//
// Route table:
//   /login          → Login (public)
//   /admin/*        → RouteGuard → CompAdminShell (comp_admin)
//   /rep/*          → RouteGuard → RepShell
//   /manager/*      → RouteGuard → ManagerShell
//   /finance/*      → RouteGuard → FinanceShell
//   /tenant-admin/* → RouteGuard → TenantAdminShell
//   /onboarding/*   → RouteGuard → OnboardingShell (comp_admin, tenant_super_admin)
//   /               → Navigate to /login
//   *               → Navigate to /login
// ---------------------------------------------------------------------------
export default function App() {
  return (
    <FeatureProvider>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/auth/callback" element={<SsoCallback />} />
      <Route path="/sso" element={<SsoHandoff />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/magic-link" element={<MagicLinkLanding />} />

      <Route
        path="/admin/*"
        element={
          <RouteGuard>
            <CompAdminShell />
          </RouteGuard>
        }
      />

      <Route
        path="/rep/*"
        element={
          <RouteGuard>
            <RepShell />
          </RouteGuard>
        }
      />

      <Route
  path="/manager/*"
  element={
    <RouteGuard>
      <ManagerShell />
    </RouteGuard>
  }
/>

      <Route
        path="/finance/*"
        element={
          <RouteGuard>
            <FinanceShell />
          </RouteGuard>
        }
      />

      <Route
        path="/tenant-admin/*"
        element={
          <RouteGuard>
            <TenantAdminShell />
          </RouteGuard>
        }
      />

      <Route
        path="/onboarding/*"
        element={
          <RouteGuard>
            <OnboardingShell />
          </RouteGuard>
        }
      />

       <Route
        path="/platform-admin/*"
        element={
          <RouteGuard allowedRoles={["tool_admin"]}>
            <PlatformAdminShell />
          </RouteGuard>
        }
      />

      {/* Root and catch-all — RouteGuard handles auth→shell redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
    </FeatureProvider>
  );
}
